import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Button from '@spothero/ui/button';
import Checkbox from '@spothero/ui/checkbox';
import Form from '@spothero/ui/form';
import FormGroup from '@spothero/ui/form-group';
import FormGroupError from '@spothero/ui/form-group-error';
import Label from '@spothero/ui/label';
import TextButton from '@spothero/ui/text-button';
import TextInput from '@spothero/ui/text-input';
import APIUtils from '@spothero/utils/api';
import EnvironmentUtils from '@spothero/utils/environment';
import VehicleAPI from 'api/vehicle';
import UserUtils from 'user/user-utils';
import SegmentUtils from 'utils/segment';
import VehicleSelect from './VehicleSelect';

export default class AddNewVehicleForm extends Component {
    static propTypes = {
        user: PropTypes.object,
        showTitle: PropTypes.bool,
        showCTAs: PropTypes.bool,
        formErrors: PropTypes.array,
        onSelectChange: PropTypes.func,
        onVehicleDataRetrieved: PropTypes.func,
        onVehicleSaveSuccess: PropTypes.func,
        onVehicleSaveError: PropTypes.func,
        onNevermindClick: PropTypes.func,
        setOversizeFeeError: PropTypes.func,
        vehicleSelectErrorsInline: PropTypes.bool,
        useVehicleSelectLabels: PropTypes.bool,
        showSaveAsDefault: PropTypes.bool,
        allowManualEntry: PropTypes.bool,
        fromAccount: PropTypes.bool
    };
    static defaultProps = {
        showSaveAsDefault: true
    };
    state = {
        areActionsDisabled: false,
        formError: null,
        showManualEntry: false
    };

    constructor(props) {
        super(props);

        this._isMobile = EnvironmentUtils.isMobileDevice();
    }

    componentWillUnmount() {
        if (this._addVehicleLoader) {
            this._addVehicleLoader.cancel();
            this._addVehicleLoader = null;
        }
    }

    _onVehicleSelectChange = data => {
        const {
            make,
            model,
            year
        } = data;
        const {
            onSelectChange,
            onVehicleDataRetrieved
        } = this.props;

        if (make && model && year && onVehicleDataRetrieved) {
            onVehicleDataRetrieved(data);
        } else if (onSelectChange) {
            onSelectChange(data);
        }
    }

    _onSubmit = evt => {
        evt.preventDefault();

        const formData = this.submit();

        if (!formData) { return; }

        this.setState({
            areActionsDisabled: true,
            formError: null
        });

        this._addVehicle(formData);
    }

    _onManualEntryToggle = () => {
        this.setState((prevState, props) => {
            return {
                showManualEntry: !prevState.showManualEntry
            };
        });
    }

    async _addVehicle(formData) {
        const {
            fromAccount,
            onVehicleSaveSuccess,
            onVehicleSaveError
        } = this.props;
        const {
            make,
            model,
            year,
            saveVehicleAsDefault
        } = formData;

        try {
            this._addVehicleLoader = APIUtils.makeCancelable(
                VehicleAPI.add({
                    make,
                    model,
                    year,
                    is_default: saveVehicleAsDefault // eslint-disable-line camelcase
                })
            );

            const {
                data
            } = await this._addVehicleLoader.promise;

            this.setState({
                areActionsDisabled: false
            });

            SegmentUtils.track({
                event: 'Account Step Completed',
                properties: {
                    'vehicle updated': true,
                    'from screen': (fromAccount) ? 'account' : 'checkout'
                }
            });

            onVehicleSaveSuccess({
                id: data.vehicle_info_id,
                make: data.make,
                makeLabel: data.make_pretty,
                model: data.model,
                modelLabel: data.model_pretty,
                year: data.year.toString(),
                isDefault: data.is_default
            });
        } catch (error) {
            if (error.data && error.data.data) {
                error.data = error.data.data;
            }

            const errors = error.data.errors;

            this.setState({
                areActionsDisabled: false,
                formError: errors[0].messages
            });

            SegmentUtils.track({
                event: 'Error Message Displayed',
                properties: {
                    'error message': `Oops! We couldn't save that vehicle.`,
                    'error screen': (fromAccount) ? 'account' : 'checkout',
                    'error action': 'updating vehicle'
                }
            });

            if (onVehicleSaveError) {
                onVehicleSaveError(errors);
            }
        }

        this._addVehicleLoader = null;
    }

    submit() {
        const vehicleSelectData = (this._vehicleSelectForm)
            ? this._vehicleSelectForm.submit()
            : false;

        if (vehicleSelectData && vehicleSelectData.saveVehicleAsDefault) {
            vehicleSelectData.saveVehicleAsDefault = Boolean(vehicleSelectData.saveVehicleAsDefault);
        }

        if (vehicleSelectData || this.state.showManualEntry) {
            return {
                ...vehicleSelectData,
                ...this._newVehicleForm.submit()
            };
        }

        return false;
    }

    render() {
        const {
            user,
            showTitle,
            showCTAs,
            formErrors,
            onNevermindClick,
            setOversizeFeeError,
            vehicleSelectErrorsInline,
            useVehicleSelectLabels,
            showSaveAsDefault,
            allowManualEntry
        } = this.props;
        const {
            areActionsDisabled,
            formError,
            showManualEntry
        } = this.state;
        const errors = (formErrors)
            ? formErrors
            : formError;
        const manualEntryToggleText = (showManualEntry)
            ? 'Return to standard list of vehicles'
            : `I don't see my vehicle`;

        return (
            <div className="AddNewVehicleForm">
                {showTitle && <h2 className="subtitle">Add a Vehicle</h2>}
                {!showManualEntry &&
                    <VehicleSelect
                        ref={node => { this._vehicleSelectForm = node; }}
                        onChange={this._onVehicleSelectChange}
                        setOversizeFeeError={setOversizeFeeError}
                        errorsInline={vehicleSelectErrorsInline}
                        useLabels={useVehicleSelectLabels}
                    />
                }
                <Form
                    ref={node => { this._newVehicleForm = node; }}
                    id="AddNewVehicleForm-form"
                    onSubmit={this._onSubmit}
                >
                    {(!showManualEntry && user.status === UserUtils.AUTH_STATE.USER && showSaveAsDefault) &&
                        <FormGroup>
                            <Checkbox
                                id="AddNewVehicleForm-save-as-default"
                                name="saveVehicleAsDefault"
                                label={<Label>Make this my default vehicle</Label>}
                                defaultChecked
                            />
                        </FormGroup>
                    }
                    {showManualEntry &&
                        <FormGroup
                            className="AddNewVehicleForm-manual-entry"
                            inline={!this._isMobile}
                        >
                            <TextInput
                                className="AddNewVehicleForm-manual-entry-make"
                                name="make"
                                label={(!this._isMobile) ? <Label>Make</Label> : null}
                                placeholder={(this._isMobile) ? 'Enter Make' : ''}
                                required
                            />
                            <TextInput
                                className="AddNewVehicleForm-manual-entry-model"
                                name="model"
                                label={(!this._isMobile) ? <Label>Model</Label> : null}
                                placeholder={(this._isMobile) ? 'Enter Model' : ''}
                                required
                            />
                            <TextInput
                                className="AddNewVehicleForm-manual-entry-year"
                                name="year"
                                label={(!this._isMobile) ? <Label>Year</Label> : null}
                                placeholder={(this._isMobile) ? 'Enter Year' : ''}
                                maxLength={4}
                                mask="9999"
                                required
                            />
                        </FormGroup>
                    }
                    {errors &&
                        <FormGroupError>
                            {errors}
                        </FormGroupError>
                    }
                    {allowManualEntry &&
                        <p>
                            <TextButton
                                className="AddNewVehicleForm-manual-entry-toggle"
                                onClick={this._onManualEntryToggle}
                            >
                                {manualEntryToggleText}
                            </TextButton>
                        </p>
                    }
                    {showCTAs &&
                        <div className="FormActions">
                            <Button
                                type="submit"
                                color="primary"
                                disabled={areActionsDisabled}
                                loading={areActionsDisabled}
                            >
                                Add Vehicle
                            </Button>
                            <TextButton
                                disabled={areActionsDisabled}
                                onClick={onNevermindClick}
                            >
                                Nevermind
                            </TextButton>
                        </div>
                    }
                </Form>
            </div>
        );
    }
}
